import Drift from 'drift-zoom';
import Flickity from 'flickity';
import EventHandler from '@pixelunion/events';
import rimg from 'rimg-shopify';

import layout from '../Layout';
import Video from './Video';
import ProductClicktoZoom from './ProductClickToZoom';

export default class ProductGallery {
  constructor({ el, settings, isQuickshop }) {
    this.el = el;
    this.settings = settings;
    this.isQuickshop = isQuickshop || false;

    this.slider = this.el.querySelector('[data-product-slider]');
    this.slides = this.slider.querySelectorAll('[data-product-image]');
    this.videoSlides = this.slider.querySelectorAll('[data-product-image-video]');
    this.zoomButton = this.slider.querySelector('[data-gallery-expand]');
    this.thumbnailsWrapper = this.el.querySelector('[data-product-slider-pagination]');
    this.thumbnails = this.el.querySelectorAll('[data-product-thumbnail]');

    this.selectedSlide = this.slider.querySelector('.image--selected');
    this.selectedThumbnail = this.thumbnailsWrapper.querySelector('.thumbnail--selected');
    this.selectedVideo = null;
    this.selectedIndex = Array.prototype.indexOf.call(this.slides, this.selectedSlide);

    this.variantChange = this._onVariantChange.bind(this);
    this._screenResize = this._screenResize.bind(this);

    this.events = new EventHandler();

    this.events.register(this.thumbnailsWrapper, 'click', e => this._thumbnailClicked(e));
    layout.onBreakpointChange(this._screenResize);

    if (this.settings.click_to_zoom !== 'disabled') {
      this.productZoom = null;

      const zoomClicked = e => {
        e.stopPropagation();
        this._openProductZoom();
      };

      this.events.register(this.zoomButton, 'click', e => zoomClicked(e));
      this.events.register(this.slider, 'click', e => zoomClicked(e));
    }

    if (this.selectedSlide.hasAttribute('data-product-image-video')) {
      const videoEl = this.selectedSlide.querySelector('[data-video]');
      this.selectedVideo = new Video(videoEl);
    } else if (this.settings.hover_zoom !== 'disabled') {
      if (layout.isGreaterThanBreakpoint('L', true)) {
        this._setupHoverZoom();
      }
    }

    if (layout.isLessThanBreakpoint('S')) {
      this._setupFlickity();
    }

    if (this.isQuickshop) {
      rimg.watch(this.el);
    }

    this._adjustGalleryPositioning();
  }

  _screenResize() {
    if (layout.isGreaterThanBreakpoint('S', true)) {
      this._disableFlickity();
    } else {
      this._setupFlickity();
    }

    if (layout.isGreaterThanBreakpoint('L', true) && !this.drift) {
      this._setupHoverZoom();
    } else if (layout.isLessThanBreakpoint('L')) {
      this._disableHoverZoom();
    }

    this._adjustGalleryPositioning();
  }

  _changeSelectedIndex(index) {
    if (this.flickity) {
      this.flickity.select(index);
    } else {
      const slide = this.slides[index];
      this._selectSlide(slide);
    }
  }

  _selectSlide(slide) {
    this.selectedSlide.classList.remove('image--selected');
    this.selectedSlide = slide;
    this.selectedSlide.classList.add('image--selected');

    const imageIndex = this.selectedSlide.dataset.productImageIndex;
    this.selectedIndex = parseInt(imageIndex, 10);

    this.selectedThumbnail.classList.remove('thumbnail--selected');
    this.selectedThumbnail = this.thumbnails[imageIndex];
    this.selectedThumbnail.classList.add('thumbnail--selected');

    if (this.selectedVideo) {
      this.selectedVideo.unload();
    }
    this.selectedVideo = null;

    this._disableHoverZoom();

    if (this.selectedSlide.hasAttribute('data-product-image-video')) {
      const videoEl = this.selectedSlide.querySelector('[data-video]');
      this.selectedVideo = new Video(videoEl);
      this.selectedVideo.onPauseClick();
      if (this.settings.enable_video_autoplay) {
        this.selectedVideo.autoplay();
      }
    } else if (this.settings.hover_zoom !== 'disabled') {
      if (layout.isGreaterThanBreakpoint('L', true)) {
        this._setupHoverZoom();
      }
    }

    this._adjustGalleryPositioning();
  }

  _onVariantChange(variant) {
    if (!variant.featured_image) {
      return;
    }

    const slide = this.slider.querySelector(`[data-product-image="${variant.featured_image.id}"]`);
    this._changeSelectedIndex(slide.dataset.productImageIndex);
  }

  _setupHoverZoom() {
    if (document.documentElement.classList.contains('has-touch')) return;

    const selectedImage = this.selectedSlide.querySelector('[data-rimg]');
    selectedImage.setAttribute('data-zoom', this.selectedSlide.getAttribute('data-zoom'));

    this.drift = new Drift(selectedImage, {
      paneContainer: this.settings.hover_zoom === 'separate'
        ? this.el.querySelector('[data-zoomed-image]')
        : this.selectedSlide,
      inlinePane: false,
      hoverBoundingBox: this.settings.hover_zoom === 'separate',
      handleTouch: false,
      onShow: () => {
        if (this.settings.hover_zoom === 'separate') {
          const productMain = document.querySelector('.product-main');
          const productAlt = document.querySelector('.product-form--alt');
          productMain.classList.add('product-gallery--fade');
          if (productAlt) productAlt.classList.add('product-gallery--fade');
        }
      },
      onHide: () => {
        if (this.settings.hover_zoom === 'separate') {
          const productMain = document.querySelector('.product-main');
          const productAlt = document.querySelector('.product-form--alt');
          productMain.classList.remove('product-gallery--fade');
          if (productAlt) productAlt.classList.remove('product-gallery--fade');
        }
      },
    });
  }

  _disableHoverZoom() {
    if (this.drift) {
      this.drift.disable();
      this.drift = null;
    }
  }

  _setupFlickity() {
    if (this.flickity) return;

    this.flickity = new Flickity(this.slider, {
      accessibility: false,
      adaptiveHeight: true,
      cellSelector: '.product-gallery--image',
      contain: true,
      friction: 0.5,
      freeScroll: false,
      initialIndex: this.selectedIndex,
      lazyLoad: false,
      percentPosition: false,
      prevNextButtons: false,
      pageDots: false,
      selectedAttraction: 0.1,
      setGallerySize: false,
      wrapAround: true,
    });

    this.flickityEvent = this.events.register(this.slider, 'rimg:load', () => {
      this._setSlideHeight();
    });

    this.flickity.on('change', index => {
      this._selectSlide(this.slides[index]);
    });

    this.flickity.on('pointerDown', () => {
      // Removes the focus ring when swiping with touch device
      this.slider.blur();
    });

    // We need to disable the product zoom open if a user is swiping
    // This was an issue on mouse gestures, not on touch
    if (this.settings.click_to_zoom !== 'disabled'
      && document.documentElement.classList.contains('no-touch')) {
      this.flickity.on('dragEnd', () => {
        this.swiped = true;
      });
    }

    this._setSlideHeight();
  }

  _disableFlickity() {
    if (this.flickity) {
      this.slider.classList.remove('flickity-enabled');
      this.flickity.destroy();
      this.flickity = null;
      this.events.unregister(this.flickityEvent);
    }
  }

  _thumbnailClicked(e) {
    let button;

    // The target will be the button itself if the user is using a keyboard to navigate, but
    // otherwise it will be the image that is clicked on.
    if (e.target.classList.contains('product-gallery--thumbnail-trigger')) {
      button = e.target;
    } else if (e.target.parentNode.parentNode.classList
      .contains('product-gallery--thumbnail-trigger')) {
      button = e.target.parentNode.parentNode;
    } else {
      return;
    }

    this._changeSelectedIndex(button.getAttribute('data-product-thumbnail'));
  }

  _setSlideHeight() {
    this.thumbnailsWrapper.classList.remove('loading');

    // Use the stored image height for situations where the DOM is in a midway state.
    const containerHeight = this.storedImageHeight
      || this.selectedSlide.getBoundingClientRect().height;
    this.storedImageHeight = null;

    this.slider.style.height = `${containerHeight}px`;
  }

  _adjustGalleryPositioning() {
    if (this.settings.thumbnail_position === 'left') {
      if (this.isQuickshop) {
        if (layout.isGreaterThanBreakpoint('L', true)) {
          this.el.classList.add('product-gallery--thumbs-left');
        } else {
          this.storedImageHeight = this.selectedSlide.getBoundingClientRect().height;
          this.el.classList.remove('product-gallery--thumbs-left');
        }
      }

      // We need to adjust the width of the thumbnails if they are positioned left
      // and they wrap into 2 columns. CSS flexbox does not cause the width of the
      // element to stretch. Looks like it's a problem with flexbox when wrapping columns:
      // https://stackoverflow.com/questions/33891709/when-flexbox-items-wrap-in-column-mode-container-does-not-grow-its-width
      if (layout.isGreaterThanBreakpoint('L', true)
        || (layout.isGreaterThanBreakpoint('S', true) && !this.isQuickshop)) {
        if (this.el.firstChild !== this.thumbnailsWrapper) {
          const activeButton = document.activeElement;
          this.el.insertBefore(this.thumbnailsWrapper, this.slider);
          activeButton.focus(); // This prevents loss of focus if one of the thumbnails are selected
        }

        // When switching layouts in the editor sometimes we need to wait for a few milliseconds
        // before positioning the thumbs.
        setTimeout(() => {
          const leftmostBounds = this.thumbnails[0].getBoundingClientRect();
          const thumbStyle = this.thumbnails[0].currentStyle
            || window.getComputedStyle(this.thumbnails[0]);
          const thumbMargin = (parseInt(thumbStyle.marginLeft, 10)) * 2;
          const wrapped = Array.prototype.some.call(this.thumbnails,
            thumb => thumb.getBoundingClientRect().left > leftmostBounds.left);
          if (wrapped) {
            this.thumbnailsWrapper.style.width = `${(leftmostBounds.width + thumbMargin) * 2}px`;
            this.thumbnailsWrapper.style.maxWidth = `${(leftmostBounds.width + thumbMargin) * 2}px`;
          }
          this._setSlideHeight();
        }, 100);
      } else {
        if (this.el.firstElementChild === this.thumbnailsWrapper) {
          this.el.appendChild(this.thumbnailsWrapper);
        }
        this.thumbnailsWrapper.style.width = '';
        this.thumbnailsWrapper.style.maxWidth = '';
        this._setSlideHeight();
      }

      if (layout.isLessThanBreakpoint('S')) {
        this._adjustMobileThumbPosition();
      }
    } else {
      this._setSlideHeight();
      this._adjustMobileThumbPosition();
    }
  }

  _adjustMobileThumbPosition() {
    // This brings the thumbnail into view if it is not visible on the screen after
    // a user swipes to a slide that it is tied to. Only important on mobile.
    if (layout.isLessThanBreakpoint('S')) {
      const thumbBounds = this.selectedThumbnail.getBoundingClientRect();
      const thumbWrapperBounds = this.thumbnailsWrapper.getBoundingClientRect();

      if (this.selectedThumbnail.offsetLeft + thumbBounds.width
        - this.thumbnailsWrapper.scrollLeft > thumbWrapperBounds.width) {
        this.thumbnailsWrapper.scrollLeft = this.selectedThumbnail.offsetLeft
          + thumbBounds.width - thumbWrapperBounds.width + 10;
      } else if (this.selectedThumbnail.offsetLeft < this.thumbnailsWrapper.scrollLeft) {
        this.thumbnailsWrapper.scrollLeft = this.selectedThumbnail.offsetLeft - 10;
      }
    }
  }

  _openProductZoom() {
    if (this.swiped) {
      this.swiped = false;
      return;
    }

    if (this.selectedSlide.hasAttribute('data-product-image-video')
      || (layout.isGreaterThanBreakpoint('S', true) && this.settings.click_to_zoom === 'mobile')
      || (layout.isLessThanBreakpoint('S') && this.settings.click_to_zoom === 'desktop')) {
      return;
    }

    this.productZoom = new ProductClicktoZoom({
      settings: this.settings,
      slides: this.slides,
      originalThumbnails: this.thumbnails,
      startIndex: this.selectedIndex,
      selectGalleryIndex: index => this._changeSelectedIndex(index),
    });
  }

  unload() {
    this.events.unregisterAll();

    layout.offBreakpointChange(this._screenResize);

    if (this.isQuickshop) {
      rimg.unwatch(this.el);
    }

    if (this.selectedVideo) {
      this.selectedVideo.unload();
    }

    this._disableHoverZoom();
    this._disableFlickity();

    if (this.productZoom) {
      this.productZoom.unload();
    }
  }
}
